/**
 * "Dismiss" — take a PAID order out of the Active queue without issuing a coupon.
 *
 * For orders whose code was given to the customer by hand before this app existed: the ledger
 * never learned about it, so the row would otherwise sit in the queue forever and the only way
 * to clear it would be to mint a second code.
 *
 * ROOT ONLY. Hidden (not greyed out) for everyone else, the same reasoning as
 * DeactivateAccountButton. The server enforces the role and refuses anything not paid regardless,
 * so hiding it is tidiness, not a control. The row stays under All orders, where root can restore it.
 */
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@staff-app/ui';
import { EyeOff, Undo2 } from 'lucide-react';
import type { DeviceOrderTask } from '@staff-app/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { isPaidStatus } from '../lib/status';
import { useDismissDeviceOrderTask, useRestoreDeviceOrderTask } from '../hooks/useOrderDeviceTasks';

const DEFAULT_REASON = 'Coupon provided manually before the app';

export function useIsOrderRoot(): boolean {
  const roles = useAuthStore((s) => s.adminUser?.roles);
  return Boolean(roles?.includes('root'));
}

export function DismissOrderButton({ task }: { task: DeviceOrderTask }) {
  const isRoot = useIsOrderRoot();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(DEFAULT_REASON);
  const [dismiss, { loading, error }] = useDismissDeviceOrderTask();

  if (!isRoot || !isPaidStatus(task.status) || task.dismissedAt) return null;

  const submit = async () => {
    try {
      await dismiss({ variables: { invoiceId: task._id, reason: reason.trim() } });
      setOpen(false);
    } catch {
      // Shown via `error` below; the dialog stays open so the refusal can be read.
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        className="gap-1"
        title="Remove from the Active queue — the coupon was already provided outside the app"
        onClick={() => {
          setReason(DEFAULT_REASON);
          setOpen(true);
        }}
        data-testid="order-dismiss"
      >
        <EyeOff className="h-3.5 w-3.5" />
        {'Dismiss'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{'Dismiss this paid order?'}</DialogTitle>
            <DialogDescription>
              {'It leaves the Active queue without a coupon being issued in the app. The order '}
              {'stays under All orders, and a root user can restore it there.'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3 text-sm">
            <div className="text-muted-foreground">
              {'Invoice '}
              <span className="font-mono">{task.invoiceId || task._id}</span>
              {task.ownerEmail || task.customerEmail ? ` · ${task.ownerEmail || task.customerEmail}` : ''}
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">{'Reason (recorded as an order note)'}</span>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="order-dismiss-reason"
              />
            </label>
            {error ? (
              <p className="text-sm text-destructive" data-testid="order-dismiss-error">
                {error.message}
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={loading}>
              {'Cancel'}
            </Button>
            <Button
              onClick={() => { void submit(); }}
              disabled={loading || !reason.trim()}
              data-testid="order-dismiss-confirm"
            >
              {loading ? 'Dismissing…' : 'Dismiss'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * A dismissed order's state, shown in place of the mint button. Everyone sees the label (it
 * explains why a paid order has no code); only root gets Restore.
 */
export function DismissedOrderState({ task }: { task: DeviceOrderTask }) {
  const isRoot = useIsOrderRoot();
  const [restore, { loading }] = useRestoreDeviceOrderTask();
  const when = task.dismissedAt ? new Date(task.dismissedAt).toLocaleDateString() : '';
  return (
    <span className="flex items-center gap-1">
      <span
        className="text-xs text-muted-foreground"
        title={`Dismissed ${when} by ${task.dismissedBy || 'unknown'}${
          task.dismissedReason ? ` — "${task.dismissedReason}"` : ''
        }`}
        data-testid="order-dismissed"
      >
        {'Dismissed'}
      </span>
      {isRoot ? (
        <Button
          size="sm"
          variant="ghost"
          className="h-7 gap-1 px-2"
          disabled={loading}
          title="Put this order back in the Active queue"
          onClick={() => { void restore({ variables: { invoiceId: task._id } }).catch(() => undefined); }}
          data-testid="order-restore"
        >
          <Undo2 className="h-3.5 w-3.5" />
          {'Restore'}
        </Button>
      ) : null}
    </span>
  );
}
